import { Attachment } from "@/src/components/ui-kit/attachment-viewer";
import { useAuth } from "@/src/contexts/auth.context";
import { Post } from "@/src/models/post.model";
import { UserPhoto } from "@/src/models/user.model";
import {
  useGetUserPostsQuery,
  useSavedPostsQuery,
} from "@/src/services/api/posts/posts.hooks";
import { getCountryFlag } from "@/src/constants/countries";
import { useGetUserProfileQuery } from "@/src/services/profile/profile.hooks";
import { useBlocksUserByIdQuery } from "@/src/services/users.blocks.api";
import {
  getFileExtension,
  getFilename,
  getMimeType,
  isImageFile,
  isVideoFile,
} from "@/src/utils/attachment.utils";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const PROFILE_TABS = [
  "LABEL_POSTS",
  "LABEL_SAVED",
  "LABEL_PHOTOS",
  "LABEL_VIDEOS",
  "LABEL_FILES",
] as const;
export type ProfileTab = (typeof PROFILE_TABS)[number];

export const useProfileScreen = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { user: currentUser, isSuperAdmin, userGroups } = useAuth();
  const { id } = useLocalSearchParams();
  const profileUserId = Array.isArray(id) ? id[0] : id;
  const isOwnProfile = currentUser?.itemId === profileUserId;
  const canAccessEdit = isOwnProfile || isSuperAdmin;

  const [activeTab, setActiveTab] = useState<ProfileTab>("LABEL_POSTS");
  const [editPost, setEditPost] = useState<Post | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletePostId, setDeletePostId] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);

  const { data: blocksUser } = useBlocksUserByIdQuery(profileUserId ?? "", {
    enabled: !!profileUserId,
  });
  const { data: userProfile } = useGetUserProfileQuery(profileUserId ?? "");

  const {
    data: savedPostsData,
    isLoading: isSavedPostsLoading,
    isError: isSavedPostsError,
    refetch: refetchSavedPosts,
  } = useSavedPostsQuery({
    enabled: activeTab === "LABEL_SAVED" && isOwnProfile,
  });

  const {
    data: userPostsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: userPostsLoading,
    isError: userPostsError,
    refetch: userPostsRefetch,
  } = useGetUserPostsQuery(profileUserId ?? "");

  const allUserPosts =
    userPostsData?.pages
      .flatMap((page) => page?.getposts?.items ?? [])
      .filter(
        (post, index, self) =>
          index === self.findIndex((p) => p.ItemId === post.ItemId),
      ) ?? [];

  const visibleUserPosts = allUserPosts.filter((post) => {
    switch (post.visibility) {
      case "public":
        return true;
      case "private":
        return post.CreatedBy === currentUser?.itemId;
      case "specific":
        return post.visibleToUserIds?.includes(currentUser?.itemId ?? "");
      case "group":
        return post.visibleToGroupIds?.some((groupId: string) =>
          userGroups?.includes(groupId),
        );
      default:
        return false;
    }
  });

  // Photos → UserPhoto[]
  const userPhotos: UserPhoto[] = visibleUserPosts
    .flatMap((post) => post.attachmentUrls ?? [])
    .filter((url: string) => isImageFile(url))
    .map((url: string) => ({
      id: url,
      file_name: getFilename(url),
      file_size: null,
      mime_type: getMimeType(getFileExtension(url)),
      url,
    }));

  // Files → Attachment[] (non-media only)
  const userDocuments: Attachment[] = visibleUserPosts
    .flatMap((post) => post.attachmentUrls ?? [])
    .filter((url: string) => !isImageFile(url) && !isVideoFile(url))
    .map((url: string) => ({
      url,
      type: "file" as const,
    }));

  const userVideos: string[] = visibleUserPosts
    .flatMap((post) => post.attachmentUrls ?? [])
    .filter((url: string) => isVideoFile(url));

  const isProfileLoading = !blocksUser && !userProfile;

  const displayName = blocksUser
    ? `${blocksUser.firstName} ${blocksUser.lastName}`
    : null;

  const formattedDob = (() => {
    if (!userProfile?.dob) return t("LABEL_NOT_AVAILABLE");
    const sanitizedDob = userProfile.dob.replace(/[<>]/g, "");
    const parts = sanitizedDob.split("/");
    if (parts.length !== 3) return t("LABEL_NOT_AVAILABLE");
    const [day, month, year] = parts.map(Number);
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return t("LABEL_NOT_AVAILABLE");
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  })();
  const personalInfo = [
    {
      label: "LABEL_EMAIL",
      value: blocksUser?.email ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "Sms",
    },
    {
      label: "LABEL_PHONE",
      value: blocksUser?.phoneNumber ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "CallAdd",
    },
    { label: "LABEL_DATE_OF_BIRTH", value: formattedDob, iconName: "Calendar" },
    {
      label: "LABEL_GENDER",
      value: userProfile?.gender ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "User",
    },
    {
      label: "LABEL_ADDRESS",
      value: userProfile?.address ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "Location",
      isMultiLine: true,
    },
    {
      label: "LABEL_COUNTRY",
      value: userProfile?.country
        ? `${getCountryFlag(userProfile.country)}  ${userProfile.country}`.trim()
        : t("LABEL_NOT_AVAILABLE"),
      iconName: "Flag",
    },
  ];

  const workInfo = [
    {
      label: "LABEL_DESIGNATION",
      value: userProfile?.designation ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "Teacher",
    },
    {
      label: "LABEL_DEPARTMENT",
      value: userProfile?.department ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "RowHorizontal",
    },
    {
      label: "LABEL_COMPANY",
      value: userProfile?.company ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "Bag2",
      isMultiLine: true,
    },
    {
      label: "LABEL_WEBSITE",
      value: userProfile?.website ?? t("LABEL_NOT_AVAILABLE"),
      iconName: "Global",
      isMultiLine: true,
    },
  ];

  const visibleTabs = isOwnProfile
    ? PROFILE_TABS
    : (["LABEL_POSTS", "LABEL_PHOTOS", "LABEL_VIDEOS"] as const);

  const handleEditPost = (selectedPost: Post) => {
    setEditPost(selectedPost);
    setModalOpen(true);
  };

  const handleDeletePress = (selectedPost: Post) => {
    setDeletePostId(selectedPost.ItemId);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    setConfirmDeleteOpen(false);
    setDeletePostId(null);
  };

  const handleNavigateToEdit = () =>
    router.push({
      pathname: "/profile/[id]/edit",
      params: { id: profileUserId },
    });

  return {
    profileUserId,
    isOwnProfile,
    canAccessEdit,
    activeTab,
    setActiveTab,
    editPost,
    modalOpen,
    setModalOpen,
    setEditPost,
    deletePostId,
    confirmDeleteOpen,
    setConfirmDeleteOpen,
    infoExpanded,
    setInfoExpanded,
    blocksUser,
    userProfile,
    isProfileLoading,
    savedPostsData,
    isSavedPostsLoading,
    isSavedPostsError,
    refetchSavedPosts,
    displayName,
    personalInfo,
    workInfo,
    visibleTabs,
    handleEditPost,
    handleDeletePress,
    handleConfirmDelete,
    handleNavigateToEdit,

    userPosts: visibleUserPosts,
    userPostsLoading,
    userPostsError,
    userPostsRefetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    userPhotos,
    userDocuments,
    userVideos,
  };
};
